// The Fans data behind the Requests page: link visitors (the chip count and
// the Visitors card), the all-time fan rows the chip loads on tap, and the
// sheet's open flag. Split out of pages/RequestsPage.tsx; RequestsHeader
// shows the chip, FansModal shows the sheet.
import { useEffect, useState } from "react";
import { ordersApi } from "../services/orders";
import { usersApi } from "../services/users";
import type { VisitCounts } from "../types/orders";
import type { FanRow } from "../components/FansLeaderboardSheet";
import { useAccountCache } from "./useAccountCache";

export type VisitRange = keyof Pick<VisitCounts, "d1" | "d7" | "d30" | "all">;
// A Top Fans row plus the one thing the DJ view adds: when they last asked.
export type DjFan = FanRow & { lastSeen: string | null };
export type FansMeta = { anonymousRequests: number; totalFans: number; totalRequests: number };

export function useFans() {
  const [showFansModal, setShowFansModal] = useState(false);
  // Link Unique Visitors (founder: "should be at the top of Fans,
  // not in the leaderboard"): the 7-day number rides on the Fans chip so it
  // reads at a glance from the top of this tab, and the full card with the
  // range chips sits at the top of the Fans list. One authed fetch on mount;
  // a 401/older server just means no count and no card. The last count paints
  // at once on a later visit (hooks/useAccountCache); the very first open
  // keeps the count's room with a skeleton until it answers (visitsLoading).
  const [visits, setVisits, visitsCached] = useAccountCache<VisitCounts | null>("reqs-visits", null);
  const [visitsSettled, setVisitsSettled] = useState(false);
  const [visitRange, setVisitRange] = useState<VisitRange>("d7");
  useEffect(() => {
    let alive = true;
    usersApi
      .linkVisits()
      .then((v) => alive && setVisits(v))
      .catch(() => {})
      .finally(() => alive && setVisitsSettled(true));
    return () => {
      alive = false;
    };
  }, [setVisits]);
  const visitsLoading = !visitsCached && !visitsSettled;
  // "Your fans": all-time rows on the Top Fans identity rules —
  // photo/initial, name (Instagram-linked when claimed), requests, kept spend,
  // last request. Older servers answer with the legacy array; both shapes map
  // onto the same rows so the pop-up never breaks on a deploy skew.
  const [fans, setFans] = useState<DjFan[]>([]);
  const [fansMeta, setFansMeta] = useState<FansMeta | null>(null);
  // The Fans chip: load the rows (both server generations), then open.
  const openFans = async () => {
    try {
      const d = await ordersApi.fans();
      const raw: any[] = Array.isArray(d) ? d : Array.isArray(d?.fans) ? d.fans : [];
      setFans(
        raw
          .map((f) => ({
            name: String(f.name || f.nickname || (f.instagram ? `@${f.instagram}` : "")).trim(),
            ig: f.ig || f.instagram || null,
            igVerified: f.igVerified === true,
            avatarUrl: f.avatarUrl || null,
            requests: Number(f.requests ?? f.requestCount) || 0,
            spent: Number(f.spent) || 0,
            currency: f.currency || "",
            lastSeen: f.lastSeen || null,
          }))
          .filter((f) => f.name)
      );
      setFansMeta(
        Array.isArray(d)
          ? null
          : {
              anonymousRequests: Number(d?.anonymousRequests) || 0,
              totalFans: Number(d?.totalFans) || raw.length,
              totalRequests: Number(d?.totalRequests) || 0,
            }
      );
    } catch {
      setFans([]);
      setFansMeta(null);
    }
    setShowFansModal(true);
  };
  return { visits, visitsLoading, visitRange, setVisitRange, fans, fansMeta, showFansModal, setShowFansModal, openFans };
}
